// matchUtils.js
// Pure functions only. No React APIs.
// Assumes match object shape created by NewMatch.jsx (see your existing structure).

export function recalculateMatch(baseMatch) {
    const m = structuredClone(baseMatch)
    m.history = m.history || []

    // Reset scores & players
    const resetScore = (sc) => {
        sc.runs = 0
        sc.wickets = 0
        sc.balls = 0
        sc.extras = 0
    }
    resetScore(m.scoreA)
    resetScore(m.scoreB)

    for (const side of ['A', 'B']) {
        m.players[side].forEach(p => {
            p.runs = 0
            p.balls = 0
            p.fours = 0
            p.sixes = 0
            p.isOut = false
            p.onStrike = false
            p.overs = 0
            p.ballsInCurrentOver = 0
            p.runsConceded = 0
            p.wicketsTaken = 0
        })
    }

    m.current = { strikerIndex: 0, nonStrikerIndex: 1, bowlerIndex: 0 }
    m.batting = 'A'
    m.inning = 1
    m.finished = false
    m.target = m.target ?? null
    m.result = m.result ?? null

    // Replay history (each entry must be canonical)
    for (const entry of m.history) {
        // entry: { runs, extra, extraType, wicket, over, batsmanRuns (optional), totalRuns (optional) }
        applyBall(m, entry.runs || 0, entry.extra || false, entry.extraType || null, entry.wicket || false)
    }

    return m
}

/*
applyBall: mutates the match object `m` and updates scores/players/bowlers.
Rules implemented:
- wide: +1 + any running runs -> extras, not legal ball, batsman not credited.
- noball: +1 + any batsman runs -> extras include 1, batsman gets runs; not a legal ball.
- bye / legbye: runs added to extras and team runs, count as legal delivery; batsman not credited.
- normal delivery: batsman gets runs; counts as legal ball.
- wicket: if wicket true, counts with ball if legal (bye/legbye count as legal).
- bowler.runsConceded includes extras and batsman runs on that delivery; bowler balls do NOT increment on wide/noball.
- strike rotation at odd runs and end of over.
*/
export function applyBall(m, runs = 0, extra = false, extraType = null, wicket = false) {
    const side = m.batting
    const sc = side === 'A' ? m.scoreA : m.scoreB
    const ballsPerInnings = m.oversLimit * 6

    let legalDelivery = true
    let batsmanRuns = 0
    let extrasThisBall = 0
    let totalRuns = 0

    // Handle extras
    if (extra) {
        switch (extraType) {
            case 'wide':
                extrasThisBall = 1 + (runs || 0) // runs parameter represents running (e.g., 0-4)
                totalRuns = extrasThisBall
                legalDelivery = false
                break
            case 'noball':
                extrasThisBall = 1
                batsmanRuns = runs || 0
                totalRuns = extrasThisBall + batsmanRuns
                legalDelivery = false
                break
            case 'bye':
            case 'legbye':
                extrasThisBall = runs || 0
                totalRuns = extrasThisBall
                sc.balls += 1 // counts as legal
                break
            default:
                // Unknown extras — treat as scoring extras and legal
                extrasThisBall = runs || 0
                totalRuns = extrasThisBall
                sc.balls += 1
        }
    } else {
        batsmanRuns = runs || 0
        totalRuns = batsmanRuns
        sc.balls += 1 // legal delivery
    }

    // Update team totals and extras
    sc.runs += totalRuns
    sc.extras += extrasThisBall

    // Batsman update: only for non-wide and for noball they get runs but ball not counted
    const striker = m.players[side][m.current.strikerIndex]
    if (striker) {
        if (!extra) {
            striker.runs += batsmanRuns
            striker.balls += 1
            if (batsmanRuns === 4) striker.fours += 1
            if (batsmanRuns === 6) striker.sixes += 1
        } else if (extraType === 'noball') {
            // noball: batsman gets runs but ball does NOT count
            striker.runs += batsmanRuns
            if (batsmanRuns === 4) striker.fours += 1
            if (batsmanRuns === 6) striker.sixes += 1
            // do NOT increment striker.balls
        }
    }

    // Bowler bookkeeping
    const bowlingSide = side === 'A' ? 'B' : 'A'
    const bowler = m.players[bowlingSide][m.current.bowlerIndex]
    if (bowler) {
        // runs conceded include totalRuns (includes extras and batsman runs)
        bowler.runsConceded = (bowler.runsConceded || 0) + totalRuns

        // bowler balls increment only on legal delivery except bye/legbye count as legal (already handled by sc.balls)
        if (!extra || extraType === 'bye' || extraType === 'legbye') {
            bowler.ballsInCurrentOver = (bowler.ballsInCurrentOver || 0) + 1
            if (bowler.ballsInCurrentOver === 6) {
                bowler.overs = (bowler.overs || 0) + 1
                bowler.ballsInCurrentOver = 0
            }
        }

        if (wicket) {
            bowler.wicketsTaken = (bowler.wicketsTaken || 0) + 1
        }
    }

    // Handle wicket
    if (wicket) {
        sc.wickets += 1
        if (striker) {
            striker.isOut = true
            striker.onStrike = false
        }
        // find next batsman
        const next = m.players[side].findIndex(p => !p.isOut)
        if (next !== -1) {
            m.current.strikerIndex = next
            m.players[side][next].onStrike = true
        }
    } else if (legalDelivery) {
        // rotate on odd batsman runs only (extras not counted here except noball already handled as batsmanRuns)
        if ((batsmanRuns % 2) === 1) {
            const s = m.current.strikerIndex
            const ns = m.current.nonStrikerIndex
            m.current.strikerIndex = ns
            m.current.nonStrikerIndex = s
        }
    }

    // End of over strike swap: only based on legal delivery counts (sc.balls)
    if (legalDelivery && sc.balls > 0 && sc.balls % 6 === 0) {
        const s = m.current.strikerIndex
        const ns = m.current.nonStrikerIndex
        m.current.strikerIndex = ns
        m.current.nonStrikerIndex = s

        // optional: automatic bowler rotation handled by caller or here (we will rotate bowler index)
        m.current.bowlerIndex = (m.current.bowlerIndex + 1) % m.players[bowlingSide].length
    }

    // End of innings / match logic
    const inningsOver = sc.wickets >= (m.players[side].length - 1) || sc.balls >= ballsPerInnings
    if (inningsOver && m.inning === 1) {
        m.inning = 2
        m.batting = side === 'A' ? 'B' : 'A'
        m.target = sc.runs + 1
        // reset current to new batting team's openers
        m.current = { strikerIndex: 0, nonStrikerIndex: 1, bowlerIndex: 0 }
        m.players[m.batting][0].onStrike = true
    } else if (m.inning === 2) {
        const chasingSide = m.batting
        const chasingScore = chasingSide === 'A' ? m.scoreA : m.scoreB
        const runsNeeded = (m.target || 0) - chasingScore.runs
        const ballsRemaining = (m.oversLimit * 6) - chasingScore.balls

        if (runsNeeded <= 0) {
            // chasing side won
            m.finished = true
            m.result = `${m[chasingSide === 'A' ? 'teamA' : 'teamB'].name} won by ${(m.players[chasingSide].length - chasingScore.wickets)} wickets`
        } else if (chasingScore.wickets >= (m.players[chasingSide].length - 1) || chasingScore.balls >= ballsPerInnings) {
            m.finished = true
            const defendingSide = chasingSide === 'A' ? 'B' : 'A'
            const margin = (m.target || 0) - chasingScore.runs - 1
            m.result = `${m[defendingSide === 'A' ? 'teamA' : 'teamB'].name} won by ${margin} runs`
        }
    }
}
